/** Pure scoreboard decisions for room:state. Keeps 9.4.3 rules testable:
 *  - do not yank Golf / Shanghai / analytics into 501
 *  - do resume 501 from the menu when the room is still in a 501 match
 *  - announce each scoring seq once, including a checkout that already set mode to menu
 *  - ignore repeat snapshots (clear, calibration, publish) so they cannot replay a visit
 */

const SCORING = new Set(['score', 'bust', 'win', 'undo']);

function filterRoomText(raw) {
  return String(raw == null ? '' : raw).toUpperCase().replace(/[^A-Z0-9_-]/g, '').slice(0, 24);
}

/** Phone keystrokes. Empty stays empty so deleting a code does not snap back to HOME. */
function phoneRoomDraft(raw) {
  return filterRoomText(raw);
}

/** Phone Join. An empty box commits HOME. */
function commitPhoneRoom(raw) {
  return filterRoomText(raw) || 'HOME';
}

/**
 * iPad room box. Keystrokes never change the joined room.
 * Join / Enter / leaving the field commits a non-empty code.
 * An empty commit restores the joined room (does not jump to a different default).
 */
function commitScoreboardRoom(draft, joined, commit) {
  const current = filterRoomText(joined) || 'HOME';
  const next = filterRoomText(draft);
  if (!commit) return { draft: next, roomId: current, committed: false };
  if (!next) return { draft: current, roomId: current, committed: false };
  return { draft: next, roomId: next, committed: next !== current };
}

function scoringSeq(st) {
  const ev = st && st.lastEvent;
  if (!ev || !SCORING.has(ev.type)) return null;
  const seq = Number(ev.seq);
  return Number.isFinite(seq) ? seq : null;
}

function interpretRoomState(prev, st, localMode, opts = {}) {
  const now = opts.now || Date.now();
  const seenWinSeq = opts.seenWinSeq;
  const next = {
    hydrated: !!prev.hydrated,
    seq: prev.seq == null ? null : prev.seq,
  };
  const actions = [];
  if (!st) return { next, actions };

  const ev = st.lastEvent || null;
  const seq = scoringSeq(st);
  const isNew = seq != null && seq !== next.seq;
  // Freshness is the scoring event's own clock, not room.updatedAt.
  // Calibration, visit clear, and other snapshots bump updatedAt and must not replay a checkout.
  const eventAt = ev && Number(ev.at);
  const freshWin = !!(ev && ev.type === 'win' && Number.isFinite(eventAt) && (now - eventAt) < 12000 && (now - eventAt) > -2000);
  const live501 = st.mode === '501' && Array.isArray(st.p501) && st.p501.length > 0;
  const winAlreadySeen = !!(ev && ev.type === 'win' && seq != null && seenWinSeq != null && Number(seenWinSeq) === seq);

  const pushVisit = () => {
    if (Array.isArray(st.visit)) actions.push({ type: 'visit', visit: st.visit });
  };

  const pushScoreAnnounce = () => {
    if (!ev || winAlreadySeen) return;
    if (ev.type !== 'score' && ev.type !== 'bust' && ev.type !== 'win') return;
    actions.push({ type: 'announce', event: ev, state: st });
    if (ev.type === 'win' && (localMode === '501' || localMode === 'menu')) {
      actions.push({ type: 'winAlert', state: st, seq });
    }
  };

  if (!next.hydrated) {
    next.hydrated = true;
    if (seq != null) next.seq = seq;
    if (live501) {
      actions.push({
        type: 'apply501',
        state: st,
        skipPublish: localMode === '501' || localMode === 'menu',
      });
      if (localMode === 'menu') actions.push({ type: 'enter501' });
    }
    // Refresh must not replay the last visit. A checkout from the last few seconds still surfaces once.
    if (freshWin && !winAlreadySeen) pushScoreAnnounce();
    pushVisit();
    return { next, actions };
  }

  if (live501 && localMode === 'menu') {
    actions.push({ type: 'apply501', state: st, skipPublish: true });
    actions.push({ type: 'enter501' });
  } else if (isNew && Array.isArray(st.p501) && st.p501.length) {
    actions.push({
      type: 'apply501',
      state: st,
      skipPublish: localMode === '501' || localMode === 'menu',
    });
    if (ev && ev.type === 'undo' && (localMode === 'menu' || localMode === '501')) {
      actions.push({ type: 'enter501' });
    }
  }

  if (isNew) {
    next.seq = seq;
    pushScoreAnnounce();
  }

  pushVisit();
  return { next, actions };
}

module.exports = {
  interpretRoomState,
  phoneRoomDraft,
  commitPhoneRoom,
  commitScoreboardRoom,
  filterRoomText,
};
